import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import Switch from "components/Shared/Switch"
import {
  getDeliveryProfilesScope,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import { showAlert } from "store/reducers/alertReducer"

const GROUP_LABELS = {
  scheduled: "Scheduled delivery",
  fastMoving: "Fast-moving same-day delivery",
  other: "Delivery settings",
}

const EMPTY_CARDS = []

const getNestedValue = (obj, path) =>
  String(path || "")
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj)

const setNestedValue = (obj, path, value) => {
  const keys = String(path).split(".")
  const next = { ...(obj || {}) }
  let cursor = next
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value
      return
    }
    cursor[key] = { ...(cursor[key] || {}) }
    cursor = cursor[key]
  })
  return next
}

const buildInitialDraft = (card = {}) => {
  const draft = { ...(card.tenantSettings || {}) }
  const fields = card.fieldDefinitions || []

  fields.forEach(field => {
    const tenantValue = getNestedValue(draft, field.key)
    if (tenantValue !== undefined && tenantValue !== null && tenantValue !== "") {
      return
    }

    const resolvedValue = getNestedValue(card.resolvedProfile, field.key)
    if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== "") {
      Object.assign(draft, setNestedValue(draft, field.key, resolvedValue))
    }
  })

  return draft
}

const BrandDeliverySettingsCards = ({ settingsId = "", canEdit = false }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { deliveryProfilesScope } = useSelector(state => state.GlobalSettings)
  const [draftByBrandId, setDraftByBrandId] = useState({})
  const [savingBrandId, setSavingBrandId] = useState(null)

  const cards = useMemo(() => {
    const scopeCards = deliveryProfilesScope?.cards ?? EMPTY_CARDS
    return scopeCards
      .map(card => ({
        ...card,
        fields: (card.fieldDefinitions || []).filter(field =>
          ["tenant", "both"].includes(field.editableBy)
        ),
      }))
      .filter(card => card.fields.length > 0)
  }, [deliveryProfilesScope])

  useEffect(() => {
    dispatch(getDeliveryProfilesScope())
  }, [dispatch])

  useEffect(() => {
    const nextDraft = {}
    cards.forEach(card => {
      nextDraft[card.brandId] = buildInitialDraft(card)
    })
    setDraftByBrandId(nextDraft)
  }, [cards])

  const groupedFields = card => {
    const groups = new Map()
    card.fields.forEach(field => {
      const groupKey = field.group || "other"
      if (!groups.has(groupKey)) {
        groups.set(groupKey, [])
      }
      groups.get(groupKey).push(field)
    })
    return groups
  }

  const getFieldValue = (card, field) => {
    const draftValue = getNestedValue(draftByBrandId[card.brandId], field.key)
    if (draftValue !== undefined && draftValue !== null && draftValue !== "") {
      return draftValue
    }
    return getNestedValue(card.resolvedProfile, field.key) ?? ""
  }

  const handleFieldChange = (brandId, path, value) => {
    setDraftByBrandId(prev => ({
      ...prev,
      [brandId]: setNestedValue(prev[brandId] || {}, path, value),
    }))
  }

  const handleSave = async brandId => {
    if (!settingsId) {
      dispatch(
        showAlert({
          text: t("System settings not loaded yet."),
          type: "error",
        })
      )
      return
    }

    setSavingBrandId(brandId)
    const response = await dispatch(
      updateGlobalSettingByType({
        id: settingsId,
        type: "brandDeliverySettings",
        brandId,
        settings: draftByBrandId[brandId] || {},
      })
    )
    setSavingBrandId(null)

    const { status, msg } = response?.payload || {}
    if (status) {
      dispatch(
        showAlert({
          text: msg || t("Saved successfully"),
          type: "success",
        })
      )
      dispatch(getDeliveryProfilesScope())
    } else {
      dispatch(
        showAlert({
          text: msg ? t(msg, { defaultValue: msg }) : t("Something went wrong"),
          type: "error",
        })
      )
    }
  }

  if (!cards.length) {
    return null
  }

  return (
    <div className="mb-4">
      <h5 className="mb-3">{t("Brand delivery settings")}</h5>
      <p className="text-muted">
        {t(
          "Configure delivery timing and order rules per brand based on each brand's delivery profile."
        )}
      </p>
      <Row>
        {cards.map(card => (
          <Col lg={12} key={card.brandId} className="mb-3">
            <Card>
              <CardBody>
                <CardTitle tag="h6">{card.displayName || card.brandName}</CardTitle>
                <p className="text-muted small mb-4">
                  {card.templateName} ({card.mode})
                </p>

                {[...groupedFields(card).entries()].map(([groupKey, fields]) => (
                  <div key={`${card.brandId}-${groupKey}`} className="mb-4">
                    <h6 className="mb-3">{t(GROUP_LABELS[groupKey] || groupKey)}</h6>
                    <Row>
                      {fields.map(field => (
                        <Col md={6} key={`${card.brandId}-${field.key}`} className="mb-3">
                          {field.type === "boolean" ? (
                            <div className="d-flex justify-content-between align-items-center">
                              <Label className="col-form-label mb-0">
                                {field.label || field.key}
                              </Label>
                              <Switch
                                onClickHandler={() =>
                                  handleFieldChange(
                                    card.brandId,
                                    field.key,
                                    !getFieldValue(card, field)
                                  )
                                }
                                isChecked={Boolean(getFieldValue(card, field))}
                                disabled={!canEdit}
                              />
                            </div>
                          ) : (
                            <>
                              <Label>{field.label || field.key}</Label>
                              <Input
                                type={field.type === "number" ? "number" : field.type === "time" ? "time" : "text"}
                                min={field.type === "number" ? 0 : undefined}
                                value={getFieldValue(card, field)}
                                disabled={!canEdit}
                                onChange={e =>
                                  handleFieldChange(
                                    card.brandId,
                                    field.key,
                                    field.type === "number"
                                      ? Number(e.target.value)
                                      : e.target.value
                                  )
                                }
                              />
                            </>
                          )}
                          {field.helpText ? (
                            <small className="text-muted d-block mt-1">
                              {t(field.helpText, { defaultValue: field.helpText })}
                            </small>
                          ) : null}
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}

                {canEdit ? (
                  <div className="d-flex justify-content-end">
                    <Button
                      color="primary"
                      size="sm"
                      disabled={savingBrandId === card.brandId}
                      onClick={() => handleSave(card.brandId)}
                    >
                      {savingBrandId === card.brandId ? t("Saving...") : t("Save")}
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default BrandDeliverySettingsCards
